import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Dialog, FCWithFragments } from '@modernloop/shared/components';
import { reportError } from '@modernloop/shared/observability';
import { getExternalErrorMessage } from '@modernloop/shared/utils';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  DeleteModuleLogModal_ModuleMemberFragment,
  DeleteModuleLogModal_ModuleMemberInterviewFragment,
  ModuleMemberInterviewStatus,
  TrainingStatus,
  useInterviewModuleMemberLogDeleteMutation,
} from 'src/generated/mloop-graphql';

import { resetModuleMemberListCache } from 'src/entities/InterviewModuleMember/utils';

import { apolloCacheDeleteById } from 'src/utils/apolloHelpers';

import RoleChangeAlert from '../RoleChangeAlert';

type Props = {
  onClose: () => void;
  onDelete: () => void;
};
type Fragments = {
  moduleMember: DeleteModuleLogModal_ModuleMemberFragment;
  moduleMemberInterview: DeleteModuleLogModal_ModuleMemberInterviewFragment;
};

const DeleteModuleLogModal: FCWithFragments<Fragments, Props> = ({
  onClose,
  onDelete,
  moduleMemberInterview,
  moduleMember,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [deleteModuleLog, { loading }] = useInterviewModuleMemberLogDeleteMutation();

  const roleImpacted: TrainingStatus | null = useMemo(() => {
    if (
      moduleMemberInterview.status !== ModuleMemberInterviewStatus.DidntCount &&
      moduleMember.status === TrainingStatus.ReverseShadow &&
      moduleMemberInterview.trainingRole === TrainingStatus.Shadow &&
      moduleMember.stats?.manualCompletedAsShadow + moduleMember.stats?.completedAsShadow - 1 <
        moduleMember.shadowsRequired
    ) {
      return TrainingStatus.Shadow;
    }

    return null;
  }, [
    moduleMember.stats?.manualCompletedAsShadow,
    moduleMember.shadowsRequired,
    moduleMember.stats?.completedAsShadow,
    moduleMember.status,
    moduleMemberInterview.status,
    moduleMemberInterview.trainingRole,
  ]);

  const deleteLog = async () => {
    try {
      await deleteModuleLog({
        variables: {
          input: {
            id: moduleMemberInterview.id,
          },
        },
        refetchQueries: ['InterviewModuleMemberSidepanelQuery'],
        awaitRefetchQueries: true,
        update: (cache) => {
          apolloCacheDeleteById(cache, moduleMemberInterview.id, 'ModuleMemberManualLog');
          if (roleImpacted) {
            resetModuleMemberListCache({
              cache,
              moduleId: moduleMember.interviewModuleId,
              employeeIds: [moduleMember.employeeId],
            });
          }
        },
      });
      enqueueSnackbar(
        t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.deleteModuleLog.deleteSuccess'),
        { variant: 'success' }
      );
      onDelete();
    } catch (error) {
      reportError(error);
      enqueueSnackbar(getExternalErrorMessage(error), { variant: 'error' });
    }
  };

  return (
    <Dialog
      cancelOptions={{ label: t('hiring-dashboard-common:common.cancel'), onClick: onClose }}
      decoration={
        <img
          src="/static/images/setup/trash_modal.svg"
          alt={t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.editTrainingPlan')}
          height="64px"
          width="64px"
        />
      }
      submitOptions={{
        label: t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.deleteModuleLog.deleteLog'),
        onClick: deleteLog,
        isDisabled: loading,
        isDangerous: true,
        endIcon: loading ? <CircularProgress color="inherit" size="16px" /> : undefined,
      }}
      title={t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.deleteModuleLog.title')}
      open
      maxWidth="xs"
      onClose={onClose}
    >
      <Stack spacing={1}>
        <Typography>{t('hiring-dashboard-interviewers:interviewers.interviewer.deleteCannotBeUndone')}</Typography>
        <RoleChangeAlert roleImpacted={roleImpacted} />
      </Stack>
    </Dialog>
  );
};

DeleteModuleLogModal.fragments = {
  moduleMemberInterview: gql`
    fragment deleteModuleLogModal_moduleMemberInterview on ModuleMemberInterview {
      id
      trainingRole
      status
    }
  `,
  moduleMember: gql`
    fragment deleteModuleLogModal_moduleMember on InterviewModuleMember {
      employeeId
      interviewModuleId
      shadowsRequired
      status
      stats {
        completedAsShadow
        manualCompletedAsShadow
      }
    }
  `,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DeleteManualLogMutation = gql`
  mutation InterviewModuleMemberLogDelete($input: InterviewModuleMemberLogDeleteInput!) {
    interviewModuleMemberLogDelete(input: $input) {
      result
    }
  }
`;

export default DeleteModuleLogModal;
